/**
 * PremiumDataTable - Tabela reutilizavel com estilo premium
 *
 * EXEMPLO DE USO:
 *
 * ```jsx
 * import { PremiumDataTable } from '../common/components/premium';
 *
 * const columns = [
 *   { id: 'id', label: 'ID', width: 80, sortable: true },
 *   { id: 'name', label: 'Nome', sortable: true, render: (value, row) => (
 *     <Box>
 *       <Typography fontWeight={600}>{value}</Typography>
 *       <Typography variant="caption" color="text.secondary">{row.email}</Typography>
 *     </Box>
 *   )},
 *   { id: 'status', label: 'Status', align: 'center', render: (value) => (
 *     <Chip label={value} size="small" color={value === 'active' ? 'success' : 'default'} />
 *   )},
 *   { id: 'actions', label: 'Acoes', align: 'right', render: (_, row) => (
 *     <IconButton onClick={() => handleEdit(row.id)}><EditIcon /></IconButton>
 *   )},
 * ];
 *
 * <PremiumDataTable
 *   columns={columns}
 *   rows={data}
 *   loading={isLoading}
 *   selectable
 *   selected={selectedIds}
 *   onSelectChange={setSelectedIds}
 *   orderBy={sortColumn}
 *   order={sortDirection}
 *   onSort={handleSort}
 *   page={page}
 *   rowsPerPage={pageSize}
 *   totalCount={totalRecords}
 *   onPageChange={setPage}
 *   onRowsPerPageChange={setPageSize}
 *   onRowClick={(row) => navigate(`/detail/${row.id}`)}
 *   emptyState={<EmptyState icon={<SearchIcon />} message="Nenhum resultado" />}
 * />
 * ```
 */

import React, { memo, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Checkbox,
  Typography,
  Skeleton,
  Paper,
} from '@mui/material';
import { useTranslation } from '../../components/LocalizationProvider';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

/**
 * @param {Object} props
 * @param {Array} props.columns - Definicao das colunas [{ id, label, sortable, align, width, render }]
 * @param {Array} props.rows - Dados das linhas
 * @param {boolean} props.loading - Estado de carregamento
 * @param {boolean} props.selectable - Habilita selecao multipla
 * @param {Array} props.selected - IDs selecionados
 * @param {Function} props.onSelectChange - Callback de selecao (selectedIds) => void
 * @param {string} props.orderBy - Coluna de ordenacao atual
 * @param {string} props.order - Direcao da ordenacao ('asc' | 'desc')
 * @param {Function} props.onSort - Callback de ordenacao (columnId) => void
 * @param {number} props.page - Pagina atual (0-indexed)
 * @param {number} props.rowsPerPage - Linhas por pagina
 * @param {number} props.totalCount - Total de registros
 * @param {Function} props.onPageChange - Callback de mudanca de pagina
 * @param {Function} props.onRowsPerPageChange - Callback de mudanca de linhas por pagina
 * @param {Function} props.onRowClick - Callback ao clicar na linha (row) => void
 * @param {string} props.emptyMessage - Mensagem quando nao ha dados
 * @param {React.ReactNode} props.emptyState - Componente de estado vazio customizado
 * @param {string} props.rowKey - Campo usado como key (default: 'id')
 * @param {number} props.skeletonRows - Quantidade de skeleton rows no loading
 * @param {boolean} props.stickyHeader - Header fixo
 * @param {number|string} props.maxHeight - Altura maxima
 */
const PremiumDataTable = memo(({
  columns = [],
  rows = [],
  loading = false,
  selectable = false,
  selected = [],
  onSelectChange,
  orderBy = '',
  order = 'asc',
  onSort,
  page = 0,
  rowsPerPage = 25,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  emptyMessage = 'Nenhum registro encontrado',
  emptyState,
  rowKey = 'id',
  skeletonRows = 10,
  stickyHeader = false,
  maxHeight,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Selecao
  const isAllSelected = rows.length > 0 && selected.length === rows.length;
  const isIndeterminate = selected.length > 0 && selected.length < rows.length;

  const handleSelectAll = useCallback((event) => {
    if (event.target.checked) {
      onSelectChange?.(rows.map((row) => row[rowKey]));
    } else {
      onSelectChange?.([]);
    }
  }, [rows, rowKey, onSelectChange]);

  const handleSelectOne = useCallback((id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((s) => s !== id);
    }

    onSelectChange?.(newSelected);
  }, [selected, onSelectChange]);

  const isSelected = useCallback((id) => selected.includes(id), [selected]);

  // Ordenacao
  const handleSort = useCallback((columnId) => {
    onSort?.(columnId);
  }, [onSort]);

  // Skeleton loading
  const renderSkeleton = () => (
    <>
      {Array(skeletonRows).fill(0).map((_, index) => (
        <TableRow key={`skeleton-${index}`}>
          {selectable && (
            <TableCell padding="checkbox">
              <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 1 }} />
            </TableCell>
          )}
          {columns.map((col) => (
            <TableCell key={col.id} align={col.align || 'left'}>
              <Skeleton
                variant="text"
                width={col.skeletonWidth || '70%'}
                height={24}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  // Empty state
  const renderEmpty = () => (
    <TableRow>
      <TableCell
        colSpan={columns.length + (selectable ? 1 : 0)}
        sx={{ border: 0, py: 8 }}
      >
        {emptyState || (
          <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <TableContainer
        component={Paper}
        className="premium-table-container"
        sx={{
          maxHeight,
          backgroundColor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Table stickyHeader={stickyHeader} size="small">
          {/* Header */}
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={loading}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{
                    width: column.width,
                    minWidth: column.minWidth,
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Body */}
          <TableBody>
            {loading ? (
              renderSkeleton()
            ) : rows.length === 0 ? (
              renderEmpty()
            ) : (
              rows.map((row) => {
                const rowId = row[rowKey];
                const rowSelected = isSelected(rowId);

                return (
                  <TableRow
                    key={rowId}
                    hover
                    selected={rowSelected}
                    onClick={() => onRowClick?.(row)}
                    sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={rowSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectOne(rowId);
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align || 'left'}
                      >
                        {column.render
                          ? column.render(row[column.id], row)
                          : row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginacao */}
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => onPageChange?.(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('rowsPerPage') || 'Linhas por pagina'}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
          sx={{
            borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.06)'}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
              color: tokens?.text?.secondary,
            },
          }}
        />
      )}
    </Box>
  );
});

PremiumDataTable.displayName = 'PremiumDataTable';

export default PremiumDataTable;
